import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Sparkles, RefreshCw, Loader2, AlertOctagon, AlertTriangle, Info,
  CheckCircle2, ArrowRight, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

const PRIORITY = {
  critical:   { color: "#F87171", bg: "rgba(248,113,113,0.14)", border: "rgba(248,113,113,0.55)", icon: AlertOctagon,   label: "Critical" },
  high:       { color: "#FB923C", bg: "rgba(251,146,60,0.12)",  border: "rgba(251,146,60,0.55)",  icon: AlertTriangle, label: "High priority" },
  medium:     { color: "#FBBF24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.45)",  icon: AlertTriangle, label: "Watch" },
  low:        { color: "#60A5FA", bg: "rgba(96,165,250,0.10)",  border: "rgba(96,165,250,0.45)",  icon: Info,           label: "Low" },
  reassuring: { color: "#4ADE80", bg: "rgba(74,222,128,0.10)",  border: "rgba(74,222,128,0.45)",  icon: CheckCircle2,  label: "Reassuring" },
};

const SPECIALTY_ACCENT = {
  nephrology: "#4ADE80",
  cardiology: "#F87171",
  neurology:  "#A78BFA",
  oncology:   "#FBBF24",
};

/**
 * CrossSpecialtyInsight — the "single intelligent copilot" moment.
 *
 * Sits ABOVE the specialty tiles on the Command Center. One Claude prompt
 * over ALL specialty facts surfaces the highest-priority cross-cutting risk
 * that no single specialist would see in isolation.
 */
export default function CrossSpecialtyInsight() {
  const [data, setData] = useState(null);       // null = loading, false = hide
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async (force = false) => {
    force ? setRefreshing(true) : setLoading(true);
    try {
      const { data: r } = await api.get(
        `/platform/cross-specialty-insight${force ? "?refresh=1" : ""}`
      );
      setData(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load insight");
      setData(false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]"
           data-testid="cross-insight-loading">
        <Loader2 size={14} className="animate-spin" style={{ color: "#A78BFA" }} />
        Med Nexus is reviewing your cross-specialty picture…
      </div>
    );
  }

  if (!data || !data.insight) return null;

  const insight = data.insight;
  const priority = PRIORITY[insight.priority] || PRIORITY.medium;
  const Icon = priority.icon;

  return (
    <div
      className="ros-card"
      data-testid="cross-insight-card"
      style={{ background: priority.bg, borderColor: priority.border, borderWidth: 1 }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
               style={{ background: `${priority.color}22`, border: `1px solid ${priority.color}55` }}>
            <Icon size={18} style={{ color: priority.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                    data-testid="cross-insight-priority"
                    style={{ color: priority.color, background: `${priority.color}22`, border: `1px solid ${priority.color}55` }}>
                {priority.label}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">
                Med Nexus cross-specialty insight
              </span>
              {insight.confidence && (
                <span className="text-[10px] text-[#7A8480]">· {insight.confidence} confidence</span>
              )}
            </div>
            <div className="font-heading text-base sm:text-lg text-white mt-1.5 leading-snug"
                 data-testid="cross-insight-headline">
              {insight.headline}
            </div>

            {/* Specialty pills */}
            {insight.specialties_involved?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cross-insight-specialties">
                {insight.specialties_involved.map((s) => (
                  <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider"
                        style={{ color: SPECIALTY_ACCENT[s] || "#A3ACA7",
                                 background: `${SPECIALTY_ACCENT[s] || "#A3ACA7"}18`,
                                 border: `1px solid ${SPECIALTY_ACCENT[s] || "#A3ACA7"}40` }}>
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          data-testid="cross-insight-refresh"
          title="Regenerate insight"
          className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#272E2B] text-[#A3ACA7] hover:text-white transition-colors disabled:opacity-50 flex-shrink-0"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Expandable rationale + recommendation */}
      {(insight.rationale || insight.recommendation) && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            data-testid="cross-insight-toggle"
            className="mt-4 text-[11px] flex items-center gap-1 opacity-80 hover:opacity-100 transition-opacity"
            style={{ color: priority.color }}
          >
            <ChevronDown size={12} className={expanded ? "rotate-180 transition-transform" : "transition-transform"} />
            {expanded ? "Show less" : "Why this matters & what to do"}
          </button>

          {expanded && (
            <div className="mt-3 space-y-3 pl-13" data-testid="cross-insight-details">
              {insight.rationale && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1">
                    Clinical rationale
                  </div>
                  <div className="text-sm text-[#E5E7EB] leading-relaxed">
                    {insight.rationale}
                  </div>
                </div>
              )}
              {insight.recommendation && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7] mb-1 flex items-center gap-1">
                    <ArrowRight size={10} /> Recommended next action
                  </div>
                  <div className="text-sm text-white leading-relaxed font-medium">
                    {insight.recommendation}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div className="mt-3 flex items-center gap-2 text-[10px] text-[#7A8480]">
        <Sparkles size={10} style={{ color: "#A78BFA" }} />
        {data.model === "static-empty-state" ? "Not enough data yet" : "Med Nexus AI"}
        {data.day_key && <span>· {data.day_key}</span>}
        {data.stale ? (
          <span style={{ color: "#FBBF24" }}>· Showing last known (AI temporarily offline)</span>
        ) : data.cached && (
          <span>· Cached today</span>
        )}
      </div>
    </div>
  );
}
